/**
 * AI Tools for Accounting
 *
 * Tools that allow Marta to interact with customers, invoices,
 * and bank accounts via GraphQL.
 */

import type { ToolDefinition } from './types';
import { graphqlQuery, graphqlMutation, INVOICE_FIELDS, BANK_ACCOUNT_FIELDS } from '../../database/graphqlClient';

// =============================================================================
// GRAPHQL QUERIES & MUTATIONS
// =============================================================================

const CUSTOMER_FIELDS = `
  id
  email
  customerType
  companyName
  vatNumber
  firstName
  lastName
  fiscalCode
  phone
  pec
  address {
    street
    city
    postalCode
    province
    country
  }
  sdiRecipientCode
  paymentTerms
  notes
  isActive
  tags
  createdAt
  updatedAt
`;

const QUERIES = {
  customers: `query { customers { ${CUSTOMER_FIELDS} } }`,
  invoices: `query { invoices { ${INVOICE_FIELDS} } }`,
  bankAccounts: `query { bankAccounts { ${BANK_ACCOUNT_FIELDS} } }`,
};

const MUTATIONS = {
  createCustomer: `mutation CreateCustomer($input: CustomerInput!) { createCustomer(input: $input) { ${CUSTOMER_FIELDS} } }`,
  createInvoice: `mutation CreateInvoice($input: InvoiceInput!) { createInvoice(input: $input) { ${INVOICE_FIELDS} } }`,
};

// =============================================================================
// CUSTOMER TOOL DEFINITIONS
// =============================================================================

export const searchCustomersTool: ToolDefinition = {
  name: 'search_customers',
  description: 'Cerca clienti/fornitori. Può filtrare per nome, partita IVA, tipo.',
  category: 'accounting',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.customers,
  parameters: {
    type: 'object',
    properties: {
      searchTerm: {
        type: 'string',
        description: 'Termine di ricerca per nome, ragione sociale, email',
      },
      customerType: {
        type: 'string',
        description: 'Tipo di cliente',
        enum: ['company', 'individual', 'freelancer'],
      },
      hasVat: {
        type: 'boolean',
        description: 'Filtra per presenza partita IVA',
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 20)',
      },
    },
  },
};

export const getCustomerTool: ToolDefinition = {
  name: 'get_customer',
  description: 'Ottiene i dettagli di un cliente specifico.',
  category: 'accounting',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.customers,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID del cliente',
      },
      companyName: {
        type: 'string',
        description: 'Nome azienda (alternativa all\'ID)',
      },
    },
  },
};

export const createCustomerTool: ToolDefinition = {
  name: 'create_customer',
  description: 'Crea un nuovo cliente/fornitore.',
  category: 'accounting',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createCustomer,
  parameters: {
    type: 'object',
    properties: {
      customerType: {
        type: 'string',
        description: 'Tipo di cliente',
        enum: ['company', 'individual', 'freelancer'],
      },
      companyName: {
        type: 'string',
        description: 'Ragione sociale (per aziende)',
      },
      firstName: {
        type: 'string',
        description: 'Nome (per persone fisiche)',
      },
      lastName: {
        type: 'string',
        description: 'Cognome (per persone fisiche)',
      },
      email: {
        type: 'string',
        description: 'Email',
      },
      vatNumber: {
        type: 'string',
        description: 'Partita IVA',
      },
      fiscalCode: {
        type: 'string',
        description: 'Codice Fiscale',
      },
      phone: {
        type: 'string',
        description: 'Telefono',
      },
      pec: {
        type: 'string',
        description: 'PEC',
      },
      address: {
        type: 'object',
        description: 'Indirizzo',
        properties: {
          street: { type: 'string', description: 'Via/Piazza' },
          city: { type: 'string', description: 'Città' },
          postalCode: { type: 'string', description: 'CAP' },
          province: { type: 'string', description: 'Provincia (sigla)' },
          country: { type: 'string', description: 'Paese (default IT)' },
        },
      },
      sdiRecipientCode: {
        type: 'string',
        description: 'Codice SDI per fatturazione elettronica',
      },
      paymentTerms: {
        type: 'string',
        description: 'Termini di pagamento',
        enum: ['immediate', 'net15', 'net30', 'net60', 'net90'],
        default: 'net30',
      },
      notes: {
        type: 'string',
        description: 'Note sul cliente',
      },
      tags: {
        type: 'array',
        items: { type: 'string' },
        description: 'Tag per categorizzare',
      },
    },
    required: ['email', 'customerType'],
  },
};

// =============================================================================
// INVOICE TOOL DEFINITIONS
// =============================================================================

export const searchInvoicesTool: ToolDefinition = {
  name: 'search_invoices',
  description: 'Cerca fatture. Può filtrare per cliente, stato, periodo.',
  category: 'accounting',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.invoices,
  parameters: {
    type: 'object',
    properties: {
      customerId: {
        type: 'string',
        description: 'ID del cliente',
      },
      customerName: {
        type: 'string',
        description: 'Nome cliente per ricerca',
      },
      status: {
        type: 'string',
        description: 'Stato della fattura',
        enum: ['draft', 'sent', 'paid', 'overdue', 'cancelled'],
      },
      direction: {
        type: 'string',
        description: 'Direzione fattura',
        enum: ['in', 'out'],
      },
      fromDate: {
        type: 'string',
        description: 'Data inizio periodo (YYYY-MM-DD)',
      },
      toDate: {
        type: 'string',
        description: 'Data fine periodo (YYYY-MM-DD)',
      },
      minAmount: {
        type: 'number',
        description: 'Importo minimo',
      },
      maxAmount: {
        type: 'number',
        description: 'Importo massimo',
      },
      limit: {
        type: 'number',
        description: 'Numero massimo di risultati (default 20)',
      },
    },
  },
};

export const getInvoiceTool: ToolDefinition = {
  name: 'get_invoice',
  description: 'Ottiene i dettagli di una fattura specifica.',
  category: 'accounting',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.invoices,
  parameters: {
    type: 'object',
    properties: {
      id: {
        type: 'string',
        description: 'ID della fattura',
      },
      number: {
        type: 'string',
        description: 'Numero fattura (alternativa all\'ID)',
      },
    },
  },
};

export const createInvoiceTool: ToolDefinition = {
  name: 'create_invoice',
  description: 'Crea una nuova fattura.',
  category: 'accounting',
  operation: 'create',
  requiresConfirmation: false,
  graphqlOperation: MUTATIONS.createInvoice,
  parameters: {
    type: 'object',
    properties: {
      customerId: {
        type: 'string',
        description: 'ID del cliente',
      },
      customerName: {
        type: 'string',
        description: 'Nome cliente (mostrato in fattura)',
      },
      direction: {
        type: 'string',
        description: 'Tipo fattura',
        enum: ['in', 'out'],
        default: 'out',
      },
      date: {
        type: 'string',
        description: 'Data fattura (YYYY-MM-DD)',
      },
      dueDate: {
        type: 'string',
        description: 'Data scadenza (YYYY-MM-DD)',
      },
      items: {
        type: 'array',
        description: 'Righe della fattura',
        items: {
          type: 'object',
          properties: {
            description: { type: 'string', description: 'Descrizione' },
            quantity: { type: 'number', description: 'Quantità' },
            unitPrice: { type: 'number', description: 'Prezzo unitario' },
            vatRate: { type: 'number', description: 'Aliquota IVA (es. 22)' },
          },
          required: ['description', 'quantity', 'unitPrice', 'vatRate'],
        },
      },
      notes: {
        type: 'string',
        description: 'Note sulla fattura',
      },
      projectId: {
        type: 'string',
        description: 'ID progetto associato',
      },
    },
    required: ['customerId', 'customerName', 'date', 'items'],
  },
};

export const getInvoiceSummaryTool: ToolDefinition = {
  name: 'get_invoice_summary',
  description: 'Ottiene un riepilogo delle fatture (totali per stato, periodo, etc.).',
  category: 'accounting',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.invoices,
  parameters: {
    type: 'object',
    properties: {
      year: {
        type: 'number',
        description: 'Anno di riferimento',
      },
      month: {
        type: 'number',
        description: 'Mese di riferimento (1-12)',
      },
      direction: {
        type: 'string',
        description: 'Solo entrate (out) o uscite (in)',
        enum: ['in', 'out'],
      },
    },
  },
};

// =============================================================================
// BANK ACCOUNT TOOL DEFINITIONS
// =============================================================================

export const listBankAccountsTool: ToolDefinition = {
  name: 'list_bank_accounts',
  description: 'Elenca i conti bancari.',
  category: 'accounting',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.bankAccounts,
  parameters: {
    type: 'object',
    properties: {
      accountType: {
        type: 'string',
        description: 'Tipo di conto',
        enum: ['bank', 'cash', 'credit_card', 'other'],
      },
      isActive: {
        type: 'boolean',
        description: 'Solo conti attivi',
      },
    },
  },
};

export const getBankBalanceTool: ToolDefinition = {
  name: 'get_bank_balance',
  description: 'Ottiene il saldo totale di tutti i conti o di un conto specifico.',
  category: 'accounting',
  operation: 'read',
  requiresConfirmation: false,
  graphqlOperation: QUERIES.bankAccounts,
  parameters: {
    type: 'object',
    properties: {
      accountId: {
        type: 'string',
        description: 'ID del conto specifico (opzionale)',
      },
    },
  },
};

// =============================================================================
// TOOL EXECUTION FUNCTIONS
// =============================================================================

interface Customer {
  id: string;
  email?: string;
  customerType?: string;
  companyName?: string;
  firstName?: string;
  lastName?: string;
  vatNumber?: string;
  [key: string]: unknown;
}

interface Invoice {
  id: string;
  number?: string;
  customerId?: string;
  customerName?: string;
  date?: string;
  status?: string;
  direction?: string;
  total?: number;
  [key: string]: unknown;
}

interface BankAccount {
  id: string;
  name?: string;
  accountType?: string;
  balance?: number;
  isActive?: boolean;
  [key: string]: unknown;
}

export async function executeSearchCustomers(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ customers: Customer[] }>(QUERIES.customers);
  let customers = result.customers || [];

  // Apply search term filter
  if (params.searchTerm) {
    const term = String(params.searchTerm).toLowerCase();
    customers = customers.filter(
      c =>
        c.companyName?.toLowerCase().includes(term) ||
        c.firstName?.toLowerCase().includes(term) ||
        c.lastName?.toLowerCase().includes(term) ||
        c.email?.toLowerCase().includes(term)
    );
  }

  // Apply customer type filter
  if (params.customerType) {
    customers = customers.filter(c => c.customerType === params.customerType);
  }

  // Apply VAT filter
  if (params.hasVat !== undefined) {
    customers = customers.filter(c =>
      params.hasVat ? !!c.vatNumber : !c.vatNumber
    );
  }

  // Sort by name
  customers.sort((a, b) =>
    (a.companyName || `${a.firstName} ${a.lastName}` || '').localeCompare(
      b.companyName || `${b.firstName} ${b.lastName}` || ''
    )
  );

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 20;
  customers = customers.slice(0, limit);

  return { customers, count: customers.length };
}

export async function executeGetCustomer(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ customers: Customer[] }>(QUERIES.customers);
  const customers = result.customers || [];

  let customer: Customer | undefined;

  if (params.id) {
    customer = customers.find(c => c.id === params.id);
  } else if (params.companyName) {
    const name = String(params.companyName).toLowerCase();
    customer = customers.find(c => c.companyName?.toLowerCase().includes(name));
  }

  if (!customer) {
    return { error: 'Cliente non trovato', found: false };
  }

  return { customer, found: true };
}

export async function executeCreateCustomer(params: Record<string, unknown>): Promise<unknown> {
  const address = params.address as Record<string, string> | undefined;
  const input = {
    email: params.email,
    customerType: params.customerType,
    companyName: params.companyName || null,
    vatNumber: params.vatNumber || null,
    firstName: params.firstName || null,
    lastName: params.lastName || null,
    fiscalCode: params.fiscalCode || null,
    phone: params.phone || null,
    pec: params.pec || null,
    address: address
      ? {
          street: address.street || '',
          city: address.city || '',
          postalCode: address.postalCode || '',
          province: address.province || '',
          country: address.country || 'IT',
        }
      : {
          street: '',
          city: '',
          postalCode: '',
          province: '',
          country: 'IT',
        },
    sdiRecipientCode: params.sdiRecipientCode || null,
    paymentTerms: params.paymentTerms || 'net30',
    notes: params.notes || null,
    tags: params.tags || [],
  };

  const result = await graphqlMutation<{ createCustomer: unknown }>(MUTATIONS.createCustomer, { input });
  return result.createCustomer;
}

export async function executeSearchInvoices(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ invoices: Invoice[] }>(QUERIES.invoices);
  let invoices = result.invoices || [];

  // Apply customer filters
  if (params.customerId) {
    invoices = invoices.filter(i => i.customerId === params.customerId);
  }
  if (params.customerName) {
    const name = String(params.customerName).toLowerCase();
    invoices = invoices.filter(i => i.customerName?.toLowerCase().includes(name));
  }

  // Apply status filter
  if (params.status) {
    invoices = invoices.filter(i => i.status === params.status);
  }

  // Apply direction filter
  if (params.direction) {
    invoices = invoices.filter(i => i.direction === params.direction);
  }

  // Apply date filters
  if (params.fromDate) {
    const fromDate = new Date(String(params.fromDate));
    invoices = invoices.filter(i => new Date(i.date || '') >= fromDate);
  }
  if (params.toDate) {
    const toDate = new Date(String(params.toDate));
    invoices = invoices.filter(i => new Date(i.date || '') <= toDate);
  }

  // Apply amount filters
  if (params.minAmount !== undefined) {
    invoices = invoices.filter(i => (i.total || 0) >= Number(params.minAmount));
  }
  if (params.maxAmount !== undefined) {
    invoices = invoices.filter(i => (i.total || 0) <= Number(params.maxAmount));
  }

  // Sort by date descending
  invoices.sort((a, b) => new Date(b.date || '').getTime() - new Date(a.date || '').getTime());

  // Apply limit
  const limit = typeof params.limit === 'number' ? params.limit : 20;
  invoices = invoices.slice(0, limit);

  return { invoices, count: invoices.length };
}

export async function executeGetInvoice(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ invoices: Invoice[] }>(QUERIES.invoices);
  const invoices = result.invoices || [];

  let invoice: Invoice | undefined;

  if (params.id) {
    invoice = invoices.find(i => i.id === params.id);
  } else if (params.number) {
    invoice = invoices.find(i => i.number === params.number);
  }

  if (!invoice) {
    return { error: 'Fattura non trovata', found: false };
  }

  return { invoice, found: true };
}

export async function executeCreateInvoice(params: Record<string, unknown>): Promise<unknown> {
  const items = params.items as Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    vatRate: number;
  }>;

  // Calculate totals
  let subtotal = 0;
  let vatTotal = 0;

  items.forEach(item => {
    const lineTotal = item.quantity * item.unitPrice;
    subtotal += lineTotal;
    vatTotal += lineTotal * (item.vatRate / 100);
  });

  const total = subtotal + vatTotal;

  // Generate invoice number (simple approach - in production use sequence)
  const now = new Date();
  const invoiceNumber = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${Date.now().toString().slice(-6)}`;

  const input = {
    number: invoiceNumber,
    customerId: params.customerId,
    date: params.date,
    dueDate: params.dueDate || null,
    direction: params.direction || 'out',
    customerName: params.customerName,
    subtotal,
    vatTotal,
    total,
    status: 'draft',
    items,
    projectId: params.projectId || null,
    projectName: null,
    notes: params.notes || null,
  };

  const result = await graphqlMutation<{ createInvoice: unknown }>(MUTATIONS.createInvoice, { input });
  return result.createInvoice;
}

export async function executeGetInvoiceSummary(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ invoices: Invoice[] }>(QUERIES.invoices);
  let invoices = result.invoices || [];

  // Apply year filter
  if (params.year) {
    invoices = invoices.filter(i => {
      const date = new Date(i.date || '');
      return date.getFullYear() === params.year;
    });
  }

  // Apply month filter
  if (params.month) {
    invoices = invoices.filter(i => {
      const date = new Date(i.date || '');
      return date.getMonth() + 1 === params.month;
    });
  }

  // Apply direction filter
  if (params.direction) {
    invoices = invoices.filter(i => i.direction === params.direction);
  }

  // Calculate summary
  const summary = {
    totalCount: invoices.length,
    totalAmount: invoices.reduce((sum, i) => sum + (i.total || 0), 0),
    byStatus: {
      draft: { count: 0, amount: 0 },
      sent: { count: 0, amount: 0 },
      paid: { count: 0, amount: 0 },
      overdue: { count: 0, amount: 0 },
      cancelled: { count: 0, amount: 0 },
    } as Record<string, { count: number; amount: number }>,
    byDirection: {
      in: { count: 0, amount: 0 },
      out: { count: 0, amount: 0 },
    } as Record<string, { count: number; amount: number }>,
  };

  invoices.forEach(invoice => {
    const status = invoice.status || 'draft';
    const direction = invoice.direction || 'out';
    const amount = invoice.total || 0;

    if (summary.byStatus[status]) {
      summary.byStatus[status].count++;
      summary.byStatus[status].amount += amount;
    }

    if (summary.byDirection[direction]) {
      summary.byDirection[direction].count++;
      summary.byDirection[direction].amount += amount;
    }
  });

  return summary;
}

export async function executeListBankAccounts(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ bankAccounts: BankAccount[] }>(QUERIES.bankAccounts);
  let accounts = result.bankAccounts || [];

  // Apply type filter
  if (params.accountType) {
    accounts = accounts.filter(a => a.accountType === params.accountType);
  }

  // Apply active filter
  if (params.isActive !== undefined) {
    accounts = accounts.filter(a => a.isActive === params.isActive);
  }

  // Sort by name
  accounts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  return { accounts, count: accounts.length };
}

export async function executeGetBankBalance(params: Record<string, unknown>): Promise<unknown> {
  const result = await graphqlQuery<{ bankAccounts: BankAccount[] }>(QUERIES.bankAccounts);
  const accounts = result.bankAccounts || [];

  if (params.accountId) {
    const account = accounts.find(a => a.id === params.accountId);
    if (!account) {
      return { error: 'Conto non trovato', found: false };
    }
    return {
      account: account.name,
      balance: account.balance || 0,
      found: true,
    };
  }

  // Calculate total balance
  const totalBalance = accounts
    .filter(a => a.isActive !== false)
    .reduce((sum, a) => sum + (a.balance || 0), 0);

  return {
    totalBalance,
    accountCount: accounts.filter(a => a.isActive !== false).length,
    accounts: accounts.map(a => ({
      name: a.name,
      balance: a.balance || 0,
      type: a.accountType,
    })),
  };
}

// =============================================================================
// EXPORTS
// =============================================================================

export const accountingTools: ToolDefinition[] = [
  searchCustomersTool,
  getCustomerTool,
  createCustomerTool,
  searchInvoicesTool,
  getInvoiceTool,
  createInvoiceTool,
  getInvoiceSummaryTool,
  listBankAccountsTool,
  getBankBalanceTool,
];

export const accountingToolExecutors: Record<string, (params: Record<string, unknown>) => Promise<unknown>> = {
  search_customers: executeSearchCustomers,
  get_customer: executeGetCustomer,
  create_customer: executeCreateCustomer,
  search_invoices: executeSearchInvoices,
  get_invoice: executeGetInvoice,
  create_invoice: executeCreateInvoice,
  get_invoice_summary: executeGetInvoiceSummary,
  list_bank_accounts: executeListBankAccounts,
  get_bank_balance: executeGetBankBalance,
};
